import { useTranslation } from "react-i18next"
import { Desktop, DesktopComponentRegistry } from "../common/components/Desktop"
import { useDesktop } from "../common/components/DesktopContext.js"
import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react"
import { ResizeAwareDiv } from "../common/components/ResizeablePanel"
import { createSitrep, updateSitrep, useSitRep, useSitRepDatum, useSitRepDef, useSitRepSummaries } from "../reducers/sitrepSlice.js";
import { cdate, now } from "../common/utils/CDate.js";
import useClientConfig from "../common/hooks/clientConfigHook.js";
import { buildColor, dynamicSort } from "../common/utils/tableUtilities"
import { resolveSitrepElementColor } from "../common/utils/colorUtilities.js";
import CustomForm from "../common/components/CustomForm.js";
import { Button, Dropdown } from "react-bootstrap";
import useNetwork from "../common/hooks/networkHook.js";
import useCredentials from "../common/hooks/credentialsHook.js";
import { Col, Row } from "../common/components/Grid.js";
import { DefaultButton } from "../common/components/DefaultButton.js";
import { ReactNodeDialog } from "../common/utils/dialogUtlities.js";
import TextInput from "../common/components/TextInput.js";
import DateInput from "../common/components/DateInput.js";
import TimeInput from "../common/components/TimeInput.js";
import Select from "../common/components/Select.js";
import Icon from "../common/components/Icon.js";
import { getDescriptor } from "../reducers/jobSlice.js";
import ReactForm from "../common/components/ReactForm.js";
import { useSitrepDefs } from "../reducers/sitrepDefSlice.js";
import { generateDocx } from "../common/utils/docxUtilities.js";
import { deepMerge, readField, writeField } from "../common/utils/objectUtilities.js";
import PanelTitle from "../common/components/PanelTitle.js"
import { CardCollapseButton, CardCollapseScope } from "../common/components/CardCollapseScope.js"
import Card from "../common/components/Card.js";
import { useTypes } from "../reducers/typeSlice.js"
import { memo } from "react"

const SitrepContext = createContext();

const useSelectedSitrep = () => {
    const ctx = useContext(SitrepContext);
    return ctx;
}

const NewSitRepButton = () => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const [ newSitrep, setNewSitrep ] = useState();
    const sitrepDefs = useSitrepDefs();
    const configValidationRef = useRef();
    return <>
        {!!newSitrep && <ReactNodeDialog showModal={true} onSave={() => { if (configValidationRef.current?.checkValidity()) { createSitrep(network, newSitrep);  setNewSitrep(); } }} onClose={() => setNewSitrep()} title={t('createSitrep')}>
            <ReactForm ref={configValidationRef}>
                <Row>
                    <Col>
                        <Select value={newSitrep?.defid} onChange={defid => setNewSitrep(s => ({ ...s, defid: parseInt(defid) }))} placeholder={t('kind')} required={true} labelField="kind" valueField="id" options={sitrepDefs ?? []}></Select>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <TextInput placeholder={t('title')} value={newSitrep?.title} onChange={title => setNewSitrep(s => ({ ...s, title }))} required={true}></TextInput>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <DateInput placeholder={t('startDate')} value={newSitrep?.start} onChange={(start => setNewSitrep(s => ({ ...s, start })))} required={true} tz={clientConfig.tz}></DateInput>
                    </Col>
                    <Col>
                        <TimeInput placeholder={t('startTime')} value={newSitrep?.start} onChange={(start => setNewSitrep(s => ({ ...s, start })))} required={true} tz={clientConfig.tz}></TimeInput>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <DateInput placeholder={t('endDate')} value={newSitrep?.end} onChange={(end => setNewSitrep(s => ({ ...s, end })))} required={true} tz={clientConfig.tz}></DateInput>
                    </Col>
                    <Col>
                        <TimeInput placeholder={t('endTime')} value={newSitrep?.end} onChange={(end => setNewSitrep(s => ({ ...s, end })))} required={true} tz={clientConfig.tz}></TimeInput>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <DateInput placeholder={t('reportDate')} value={newSitrep?.report_date} onChange={(report_date => setNewSitrep(s => ({ ...s, report_date })))} required={true} tz={clientConfig.tz}></DateInput>
                    </Col>
                    <Col>
                        <TimeInput placeholder={t('reportTime')} value={newSitrep?.report_date} onChange={(report_date => setNewSitrep(s => ({ ...s, report_date })))} required={true} tz={clientConfig.tz}></TimeInput>
                    </Col>
                </Row>
            </ReactForm>
        </ReactNodeDialog> }
        <DefaultButton className="w-100 mb-2" kind="add" onClick={() => setNewSitrep({})}></DefaultButton>
        </>
}

export const SitRepList = ({ showAdd = true }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const { selectedSitrep, setSelectedSitrep } = useSelectedSitrep();
    const { setComponent } = useDesktop();
    
    const sitreps = useSitRepSummaries();
    
    const displaySitreps = useMemo(() => {
        const displaySitreps = [...sitreps];
        displaySitreps.filter(element => Object.keys(element).length > 0).sort((a, b) => {
            const da = cdate(a.start), db = cdate(b.start);
            if (da.isBefore(db)) {
                return -1;
            } else if (da.isAfter(db)) {
                return 1;
            }
            return 0;
        })
        return displaySitreps;
    }, [sitreps]);
    
    const canCreate = credentials.sitrep.hasFull(['administrator', 'sitrep']);

    return <>
        <PanelTitle title={t('sitrep')}></PanelTitle>
        {canCreate && showAdd && <NewSitRepButton></NewSitRepButton>}
        {displaySitreps.map(s => <button key={s.id} type="button" onClick={() => { setSelectedSitrep(s.id); setComponent({ component: 'sitRepDetails' })}} className="p-3 w-100 d-block" style={{ border: 'none', padding: 0, textAlign: 'initial', borderTop: '1px solid black', cursor: 'pointer', ...buildColor('#ffffff', s.id === selectedSitrep, 0.2)}}>
            {s.kind ? <span style={{ fontSize: 'x-small', color: 'grey'}}>{s.kind}<br/></span> : null }
            {s.title} - {cdate(s.report_date, clientConfig.tz).dateTimeFormat(clientConfig.timezones?.enable)}
        </button>)}
    </>
}

const SitRepElement = memo(({ sitrepID, elementID, onChange }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const types = useTypes();
    const def = useSitRepDef(sitrepID, elementID);
    const color = useMemo(() => buildColor(resolveSitrepElementColor(def, types)), [def, types]);
    const bodyStyle = useMemo(() => def.style?.bodyStyle, [def]);
    const headerStyle = useMemo(() => def.style?.headerStyle ?? color, [color, def]);
    const formRef = useRef();
    const [invalid, setInvalid] = useState(false);
    const data = useSitRepDatum(sitrepID, elementID);

    let status = null;
    const canEdit = (!data?.reviewed && credentials.sitrep.hasFull(['administrator', 'sitrep'])) || (!data?.submitted && ((def.access.length > 0 && def.access.some(a => credentials.sitrep.hasFull(a))) || (def.access.length === 0 && credentials.hasFull(['administrator','sitrep']))));
    if (data?.reviewed) {
        status = t('complete')
    } else if (data?.submitted) {
        status = t('submitted');
    } else if (!canEdit) {
        status = t('pending');
    }

    const handleChange = useCallback((delta) => {
        onChange({ [elementID]: delta });
    }, [onChange, elementID]);
    
    const handleInvalidCapture = useCallback(() => {
        setInvalid(true);
    }, []);

    const actions = [];
    if (canEdit) {
        if (!data?.submitted && !data?.reviewed) {
            actions.push({ key: 'submit', variant: 'default', label: t('submit'), onClick: () => handleChange({ submitted: { username: credentials.username, timestamp: now().toISOString() }}) });
        } else if (data?.submitted && !data?.reviewed) {
            actions.push({ key: 'reviewed', variant: 'success', label: t('reviewed'), onClick: () => handleChange({ reviewed: { username: credentials.username, timestamp: now().toISOString() }}) });
            actions.push({ key: 'returned', variant: 'danger', label: t('returned'), onClick: () => handleChange({ submitted: null }) });
        }
    }
    const HeaderControls = () => {
        const wrapAction = action => event => {
            event.preventDefault();
            event.stopPropagation();
            const valid = formRef?.current?.checkValidity?.();
            setInvalid(!valid);
            if (!valid) {
                return;
            }
            action();
        };
        return <>
            {actions.map(({ key, label, variant, onClick }) => <Button key={key} className="mx-2" variant={variant} onClick={wrapAction(onClick)}>{label}</Button>)}
            {actions.length === 0 ? status : null}
        </>;
    };

    return <div onInvalidCapture={handleInvalidCapture}>
        <Card key={elementID} ref={formRef} title={<>{def.title}</>} headerStyle={headerStyle} bodyStyle={bodyStyle} headerControls={<HeaderControls />} invalid={invalid}>
            <CustomForm def={def.def} disabled={!canEdit} required={true} data={data ?? {}} onChange={handleChange}></CustomForm>
        </Card>
    </div>
})

export const SitRepDetails = () => {
    const { t } = useTranslation();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const { selectedSitrep } = useSelectedSitrep();
    const sitrep = useSitRep(selectedSitrep);
    
    const displayElements = useMemo(() => {
        if (!sitrep?.elements) {
            return [];
        }
        const elements = [...sitrep.elements];
        elements.sort(dynamicSort(['sequence','title']));
        return elements;
    }, [sitrep?.elements]);

    const doUpdate = useCallback(delta => {
        updateSitrep(network, { id: selectedSitrep, data: delta });
    }, [network, selectedSitrep]);

    const outstandingElements = useMemo(() => {
        return displayElements.filter(e => !sitrep?.data?.[e.uuid]?.reviewed);
    }, [displayElements, sitrep?.data]);

    const canApproveAll = credentials.sitrep.hasFull(['administrator', 'sitrep']) && sitrep?.enable_approve_all === true && outstandingElements.length > 0;

    const generateForm = useCallback(async (form) => {
        const jobRes = await network.fetchData('/api/v1/jobs');
        const jobs = await jobRes.json();
        const doc = await network.fetchData('/api/v1/sitrepReport/' + encodeURIComponent(form.res));
        const data = sitrep.legacy ? 
              {
                title: sitrep.title,
                report_date: sitrep.repord_date,
                start: sitrep.start,
                end: sitrep.end,
                data: sitrep.elements.filter(e => e.access.length > 0).map(({ uuid, access }) => ({ [access[0].type]: sitrep.data[uuid] })).reduce((obj, dt) => ({ ...obj, ...dt }), {}),
                types: sitrep.elements.filter(e => e.access.length > 0).map(({ def, init, access, uuid, ...props }) => ({ ...props, sitrepform: def, sitrepinit: init, sitreptables: null, internalid: uuid })),
                reports: sitrep.reports,
                summary: sitrep.elements.filter(e => e.access.length === 0).map(e => sitrep.data[e.uuid]).reduce((obj, d) => ({ ...obj, ...d }), {}),
                summarydef: sitrep.elements.filter(e => e.access.length === 0).reduce((def, e) => def.concat(e), []),
                site: sitrep.elements.find(e => e.access.length > 0).access[0].site,
                level: sitrep.elements.find(e => e.access.length > 0).access[0].level,
                kind: sitrep.kind
              }
            : sitrep.elements.reduce((obj, e) => { writeField(obj, e.field, deepMerge({}, readField(obj, e.field) ?? {}, sitrep.data[e.uuid] ?? {})); return obj; }, { 
                title: sitrep.title, 
                report_date: sitrep.report_date,
                start: sitrep.start,
                end: sitrep.end,
            });
        const result = await generateDocx(clientConfig, network, {
            template: await doc.arrayBuffer(),
            data,
            noSandbox: true,
            additionalJsContext: {
                formatDateTime: function (date) {
                    return date ? cdate(date, clientConfig.tz).dateTimeFormat(clientConfig.timezones.enable) : '';
                },
                formatDate: function (date) {
                    return date ? cdate(date, clientConfig.tz).dateFormat(clientConfig.timezones.enable) : '';
                },
                formatTime: function (date) {
                    return date ? cdate(date, clientConfig.tz).timeFormat(clientConfig.timezones.enable) : '';
                },
                orEmpty: function (data) {
                    return data !== null && data !== undefined ? data : '';
                },
                getJobDetails: function (jobid) {
                    return jobs.find(j => j.jobid == jobid);
                },
                getJobDescriptor: function (jobid) {
                    return getDescriptor(clientConfig.jobListDescriptor, jobs.find(j => j.jobid == jobid));
                }
            }
        });
        network.downloadBlob(new Blob([result.data], { type: result.mime }), (sitrep.title ?? 'sitrep') + '.' + result.type);
    }, [clientConfig, network, sitrep]);

    const approveAllOutstanding = useCallback(() => {
        if (!sitrep) {
            return;
        }
        const timestamp = now().toISOString();
        const delta = outstandingElements.reduce((toSend, element) => {
            const current = sitrep?.data?.[element.uuid] ?? {};
            if (current.reviewed) {
                return toSend;
            }
            toSend[element.uuid] = {
                reviewed: { username: credentials.username, timestamp }
            };
            if (!current.submitted) {
                toSend[element.uuid].submitted = { username: credentials.username, timestamp };
            }
            return toSend;
        }, {});
        if (Object.keys(delta).length > 0) {
            updateSitrep(network, { id: selectedSitrep, data: delta });
        }
    }, [network, outstandingElements, selectedSitrep, sitrep, credentials.username]);

    if (!sitrep) {
        return;
    }

    return <CardCollapseScope>
        <Row>
            <Col>
                {t('sitrepHeader', { start: cdate(sitrep.start, clientConfig.tz)?.dateTimeFormat(clientConfig.timezones.enable), end: cdate(sitrep.end, clientConfig.tz)?.dateTimeFormat(clientConfig.timezones.enable), report_date: cdate(sitrep.report_date, clientConfig.tz)?.dateTimeFormat(clientConfig.timezones.enable)})}
            </Col>
            <Col className="px-2" xs="auto">
                <CardCollapseButton></CardCollapseButton>
            </Col>
            <Col xs="auto">
                {canApproveAll ? <Button variant="success" onClick={approveAllOutstanding} confirmTitle={t('approveAll')} confirmMessage={t('confirmApproveAllMessage')}>{t('approveAll')}</Button> : null}
            </Col>
            <Col xs="auto">
                { sitrep.reports?.length > 0 && displayElements.every(e => sitrep.data[e.uuid].reviewed) && <Dropdown>
                    <Dropdown.Toggle variant="default">
                        <Icon intent="print"></Icon> {t('print')}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {sitrep.reports.map(form => <Dropdown.Item key={form.name} onClick={() => generateForm(form)}>{form.name}</Dropdown.Item>)}
                    </Dropdown.Menu>
                </Dropdown> }
            </Col>
        </Row>
        {displayElements.map(e => <SitRepElement key={e.uuid} sitrepID={selectedSitrep} elementID={e.uuid} onChange={doUpdate}></SitRepElement>)}
    </CardCollapseScope>;
}

const layouts = [
    {
        id: 0,
        title: '1 Column',
        defaultNarrow: true,
        columns: [
            { rows: [{ id: 'main', components: ['sitRepList'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 100, id: 1}
        ]
    },
    {
        id: 1,
        title: '2 Column',
        narrow: false,
        defaultMedium: true,
        defaultWide: true,
        columns: [
            { rows: [{ id: 'left', components: ['sitRepList'], proportion: 100 }], proportion: 25, id: 2},
            { rows: [{ id: 'main', components: ['sitRepDetails'], proportion: 90 }, { id: 'toolbar', components: ['toolbar'], proportion: 10, fixed: true }], proportion: 75, id: 1 }
        ]
    }
]

export const SitRepContext = ({ children }) => {
    const [selectedSitrep, setSelectedSitrep] = useState();
    const context = useMemo(() => ({ selectedSitrep, setSelectedSitrep }), [selectedSitrep, setSelectedSitrep]);
    return <SitrepContext.Provider value={context}>
        {children}
    </SitrepContext.Provider>
}

const SitRep = () => {
    const { t } = useTranslation();
    
    const components = useMemo(() => {
        const registry = new DesktopComponentRegistry();
        registry.addList({ id: 1, title: t('tools'), icon: { variant: 'solid', kind: 'gear' } })
        registry.register({ sid: 'sitRepList', component: SitRepList, label: t('sitrepList'), icon: { variant: 'solid', kind: 'table-list' }, primary: true, list: 1 });
        registry.register({ sid: 'sitRepDetails', component: SitRepDetails, label: t('sitrepDetails'), icon: { variant: 'solid', kind: 'table-list' }, primary: true, list: 1 });
        return registry;
    }, [t]);
    

    return <div style={{height: 'calc(100dvh - 60px)', overflow: 'auto', paddingTop: '10px'}}>
        <SitRepContext>
            <ResizeAwareDiv>
                <Desktop id="sitrep" key="desktop" layouts={layouts} components={components}></Desktop>
            </ResizeAwareDiv>
        </SitRepContext>
    </div>
}


export default SitRep;